import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, MenuItem, Box, Typography, CircularProgress, Alert,
  Checkbox, FormControlLabel, IconButton, Divider,
} from "@mui/material";
import { RequestQuoteOutlined, AddOutlined, DeleteOutlined } from "@mui/icons-material";
import { fetchQuotePrefill, createQuote, QuoteLineItem, PRIORITY_OPTIONS } from "./emailHubV3Api";

const EVENT_TYPES = ["wedding", "corporate", "birthday", "anniversary", "gala", "other"];

interface Props {
  open: boolean;
  onClose: () => void;
  threadId: number;
  onCreated: (quote: any) => void;
}

const emptyLine = (): QuoteLineItem => ({ description: "", qty: 1, unit_price: 0, total: 0 });

const CreateQuoteModal: React.FC<Props> = ({ open, onClose, threadId, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<Record<string, any>>({
    customer_name: "", email: "", phone: "", event_type: "", event_date: "",
    guest_count: "", preferred_hall: "", food_required: false, bar_required: false,
    decor_required: false, event_duration_hours: "", notes: "", terms: "",
    valid_until: "",
  });
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([emptyLine()]);

  useEffect(() => {
    if (open && threadId) {
      setLoading(true);
      setError("");
      fetchQuotePrefill(threadId)
        .then((data) => {
          setForm((prev) => ({
            ...prev,
            customer_name: data.customer_name || "",
            email: data.email || "",
            phone: data.phone || "",
            event_type: data.event_type || "",
            event_date: data.event_date || "",
            guest_count: data.guest_count ?? "",
            preferred_hall: data.preferred_hall || "",
            food_required: data.food_required || false,
            bar_required: data.bar_required || false,
            decor_required: data.decor_required || false,
            event_duration_hours: data.event_duration_hours ?? "",
            notes: data.notes || "",
          }));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, threadId]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const updateLine = (idx: number, field: keyof QuoteLineItem, value: any) => {
    setLineItems((prev) => {
      const updated = [...prev];
      (updated[idx] as any)[field] = value;
      if (field === "qty" || field === "unit_price") {
        updated[idx].total = Number(updated[idx].qty) * Number(updated[idx].unit_price);
      }
      return updated;
    });
  };

  const subtotal = lineItems.reduce((s, l) => s + (l.total || 0), 0);
  const tax = subtotal * 0.13;
  const grandTotal = subtotal + tax;

  const handleSave = async () => {
    if (!form.customer_name.trim()) { setError("Customer name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const validLines = lineItems.filter((l) => l.description.trim());
      const quote = await createQuote({
        ...form, thread_id: threadId, line_items: validLines,
        subtotal, tax_amount: tax, grand_total: grandTotal,
      });
      onCreated(quote);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to create quote.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
        <RequestQuoteOutlined sx={{ color: "#059669" }} /> Create Quote from Thread
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Customer Info */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#0d1b2a" }}>Customer Details</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <TextField label="Customer Name" value={form.customer_name}
                  onChange={handleChange("customer_name")} fullWidth required size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Email" value={form.email}
                  onChange={handleChange("email")} fullWidth size="small" type="email" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Phone" value={form.phone}
                  onChange={handleChange("phone")} fullWidth size="small" />
              </Grid>
            </Grid>

            {/* Event Info */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#0d1b2a" }}>Event Details</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={3}>
                <TextField label="Event Type" value={form.event_type}
                  onChange={handleChange("event_type")} fullWidth size="small" select>
                  <MenuItem value="">—</MenuItem>
                  {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Event Date" value={form.event_date}
                  onChange={handleChange("event_date")} fullWidth size="small"
                  type="date" InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Guest Count" value={form.guest_count}
                  onChange={handleChange("guest_count")} fullWidth size="small" type="number" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Duration (hrs)" value={form.event_duration_hours}
                  onChange={handleChange("event_duration_hours")} fullWidth size="small" type="number" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Preferred Hall" value={form.preferred_hall}
                  onChange={handleChange("preferred_hall")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box sx={{ display: "flex", gap: 2, pt: 0.5 }}>
                  <FormControlLabel control={<Checkbox checked={form.food_required} onChange={handleChange("food_required")} size="small" />} label="Food" />
                  <FormControlLabel control={<Checkbox checked={form.bar_required} onChange={handleChange("bar_required")} size="small" />} label="Bar" />
                  <FormControlLabel control={<Checkbox checked={form.decor_required} onChange={handleChange("decor_required")} size="small" />} label="Decor" />
                </Box>
              </Grid>
            </Grid>

            {/* Line Items */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0d1b2a" }}>Line Items</Typography>
              <Button startIcon={<AddOutlined />} size="small" onClick={() => setLineItems((p) => [...p, emptyLine()])}>
                Add Item
              </Button>
            </Box>
            {lineItems.map((line, idx) => (
              <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                <Grid item xs={5}>
                  <TextField placeholder="Description" value={line.description}
                    onChange={(e) => updateLine(idx, "description", e.target.value)}
                    fullWidth size="small" />
                </Grid>
                <Grid item xs={2}>
                  <TextField placeholder="Qty" value={line.qty} type="number"
                    onChange={(e) => updateLine(idx, "qty", Number(e.target.value))}
                    fullWidth size="small" />
                </Grid>
                <Grid item xs={2}>
                  <TextField placeholder="Unit Price" value={line.unit_price} type="number"
                    onChange={(e) => updateLine(idx, "unit_price", Number(e.target.value))}
                    fullWidth size="small" />
                </Grid>
                <Grid item xs={2}>
                  <TextField placeholder="Total" value={line.total.toFixed(2)}
                    fullWidth size="small" InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={1}>
                  <IconButton size="small" onClick={() => setLineItems((p) => p.filter((_, i) => i !== idx))}
                    disabled={lineItems.length <= 1}>
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
              <Typography variant="body2">Subtotal: <strong>${subtotal.toFixed(2)}</strong></Typography>
              <Typography variant="body2">HST (13%): <strong>${tax.toFixed(2)}</strong></Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>Total: ${grandTotal.toFixed(2)}</Typography>
            </Box>

            {/* Notes & Terms */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="Notes" value={form.notes}
                  onChange={handleChange("notes")} fullWidth size="small" multiline rows={2} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Terms & Conditions" value={form.terms}
                  onChange={handleChange("terms")} fullWidth size="small" multiline rows={2} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Valid Until" value={form.valid_until}
                  onChange={handleChange("valid_until")} fullWidth size="small"
                  type="date" InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : <RequestQuoteOutlined />}
          sx={{ background: "#059669", "&:hover": { background: "#047857" } }}>
          Create Quote
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateQuoteModal;
